// Blueprint's accounting system (BL-189), on catalogued parts: the QuickBooks Online or Xero company the
// ledger export goes to (exports.ledgerConnection), connecting one (the provider's consent page, then
// back), disconnecting behind a ConfirmDialog; then the export (QA-291): our accounts matched to theirs
// (Match by name, or each chosen from their chart or typed, a DataTable control cell each), a month run in
// detail or as daily summaries, and how the last runs went. Drawn by Setup › Connected systems
// (settings/ConnectionsPage.tsx) and by Accounting › Ledger and reports when the provider sends the office
// back. Each part's standing is its Section's state. Riverstone keeps Connections.tsx and LedgerExport.tsx;
// the words and state they share are ledger-connect.ts'.

import { useEffect, useRef, useState } from 'react';
import {
  Area,
  Button,
  ConfirmDialog,
  DataTable,
  Link,
  Notice,
  SelectField,
  Skeleton,
  SkeletonGroup,
  TextField,
  type DataColumn,
} from '@flowology/ui-web/fl';
import { Section } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { dayIn, dayText, monthName, todayIn } from './model.ts';
import {
  connectUrl,
  exportMonths,
  LABEL,
  MODE,
  takeLedgerReturn,
  TARGETS,
  theirLabel,
  type Connection,
  type Overview,
  type Target,
} from './ledger-connect.ts';
import { useAct, useQuery } from './ui.tsx';
import './accounting-system.css';

interface Props {
  toast: (m: string) => void;
  timeZone?: string;
}

/** While a part reads: its rows' shapes; when it didn't, why, and Try again. */
function Waiting({ error, what, onRetry }: { error: string | null; what: string; onRetry: () => void }) {
  return error ? (
    <p className="bp-acsys-bad" role="alert">
      <span>{error}</span>
      <Link onPress={onRetry}>Try again</Link>
    </p>
  ) : (
    <SkeletonGroup label={`Loading ${what}`}>
      {[0, 1].map((i) => (
        <Skeleton key={i} shape="row" width="full" />
      ))}
    </SkeletonGroup>
  );
}

export function AccountingSystem({ toast, timeZone }: Props) {
  const q = useQuery<{ connection: Connection | null }>('exports.ledgerConnection', {});
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  // back from the provider: how connecting went, once
  useEffect(() => {
    const said = takeLedgerReturn();
    if (said) toast(said);
  }, []);
  const connect = (t: Target) => {
    setBusy(true);
    void connectUrl(t)
      .then((url) => location.assign(url))
      .catch((e: unknown) => {
        toast(e instanceof Error ? e.message : 'That did not work. Try again.');
        setBusy(false);
      });
  };
  if (!q.data)
    return (
      <Section title="Accounting system">
        <Waiting error={q.error} what="the accounting system" onRetry={() => void q.reload()} />
      </Section>
    );
  const c = q.data.connection;
  return (
    <>
      <Section
        title="Accounting system"
        state={c ? { mark: 'done', word: 'Connected' } : { mark: 'waiting', word: 'Not connected' }}
      >
        <div className="bp-acsys">
          {c ? (
            <p className="bp-acsys-say">
              The ledger export goes to <strong>{LABEL[c.target]}</strong>
              {c.companyName ? <> ({c.companyName})</> : null}, connected {dayText(c.connectedAt)}.
              {c.fake ? (
                <span className="bp-acsys-muted"> A stand-in for testing: nothing leaves Flowology.</span>
              ) : null}
            </p>
          ) : (
            <p className="bp-acsys-say" data-muted="">
              Connect the company’s QuickBooks Online or Xero to export the books there. You approve it on
              their site and come back here.
            </p>
          )}
          <div className="bp-acsys-acts">
            {TARGETS.map((t) => {
              const words = c?.target === t ? `Connect ${LABEL[t]} again` : `Connect ${LABEL[t]}`;
              // one primary in a zone (DESIGN.md §3a), and only while nothing is connected: QuickBooks's
              return !c && t === 'quickbooks' ? (
                <Button key={t} variant="primary" isDisabled={busy} onPress={() => connect(t)}>
                  {words}
                </Button>
              ) : (
                <Link key={t} isDisabled={busy} onPress={() => connect(t)}>
                  {words}
                </Link>
              );
            })}
            {c ? (
              <Link isDisabled={busy} onPress={() => setAsking(true)}>
                Disconnect
              </Link>
            ) : null}
          </div>
        </div>
      </Section>
      {c ? (
        <ConfirmDialog
          isOpen={asking}
          onClose={() => setAsking(false)}
          title={`Disconnect ${LABEL[c.target]}?`}
          confirmLabel="Disconnect"
          cancelLabel="Keep it connected"
          onConfirm={async () => {
            await client.call('exports.disconnectLedger', {});
            toast(`${LABEL[c.target]} is disconnected.`);
            await q.reload();
          }}
        >
          What was exported stays there.
        </ConfirmDialog>
      ) : null}
      {c ? (
        <LedgerExportPart
          key={c.target}
          target={c.target}
          toast={toast}
          {...(timeZone ? { timeZone } : {})}
        />
      ) : null}
    </>
  );
}

type Account = Overview['accounts'][number];
type Run = Overview['runs'][number];
const STATUS: Record<Run['status'], { word: string; tone: 'muted' | 'wait' | 'bad' }> = {
  pending: { word: 'Sending…', tone: 'wait' },
  done: { word: 'Done', tone: 'muted' },
  failed: { word: 'Failed', tone: 'bad' },
};
const accountsWord = (n: number) => `${n} ${n === 1 ? 'account' : 'accounts'}`;

/** The export to the connected company: the matches, a month's run and the last runs. */
function LedgerExportPart({ target, toast, timeZone }: Props & { target: Target }) {
  const q = useQuery<Overview>('exports.overview', { target });
  const act = useAct(toast, () => q.reload());
  const today = todayIn(timeZone);
  const choices = exportMonths(today);
  // Unset: last month once the books have closed it, else this month to date.
  const [picked, setPicked] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<'summary' | 'detail'>('detail');
  const [hand, setHand] = useState<Record<string, string>>({});
  const [waiting, setWaiting] = useState<'match' | 'export' | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  // While the worker matches or sends, read again every second and a half, for up to 20 seconds.
  useEffect(() => {
    if (!waiting) return;
    const started = Date.now();
    timer.current = setInterval(() => {
      void q.reload();
      if (Date.now() - started > 20_000) setWaiting(null);
    }, 1500);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [waiting]);
  const o = q.data;
  const latest = o?.runs.find((r) => r.target === target);
  useEffect(() => {
    if (waiting === 'export' && latest && latest.status !== 'pending') {
      setWaiting(null);
      toast(
        latest.status === 'done'
          ? `Export finished · ${latest.sentCount} ${latest.sentCount === 1 ? 'entry' : 'entries'} sent`
          : `The export failed: ${latest.error ?? 'try again'}`,
      );
    }
    if (waiting === 'match' && o && o.unmapped === 0) {
      setWaiting(null);
      toast('All accounts matched.');
    }
  }, [o, latest, waiting]);

  const title = `Export to ${LABEL[target]}`;
  if (!o)
    return (
      <Section title={title}>
        <Waiting error={q.error} what="the export" onRetry={() => void q.reload()} />
      </Section>
    );
  const canEdit = o.canEdit;
  const unmatched = o.accounts.filter((a) => !a.externalAccount);
  const matched = o.accounts.filter((a) => a.externalAccount);
  const period = picked ?? (o.lastMonth.closed ? choices[1]!.value : choices[0]!.value);
  const chosen = choices.find((c) => c.value === period)!;
  const chart = o.theirChart;
  const needle = search.trim().toLowerCase();
  /** Their accounts for one of ours: the search narrows them; the one chosen always stays. */
  const options = (chosenId: string) => [
    { value: '', label: 'Choose their account…' },
    ...(chart ?? [])
      .filter((x) => !needle || x.externalId === chosenId || theirLabel(x).toLowerCase().includes(needle))
      .map((x) => ({ value: x.externalId, label: theirLabel(x) })),
  ];
  const valueOf = (a: Account) => hand[a.accountId] ?? a.externalAccount ?? '';
  const ours: DataColumn<Account> = {
    id: 'ours',
    label: 'Our account',
    rowHeader: true,
    cell: (a) => ({ text: `${a.code} ${a.name}` }),
  };
  const pick = (label: string): DataColumn<Account> => ({
    id: 'theirs',
    label,
    kind: 'control',
    cell: (a) => ({
      control: {
        label: `${LABEL[target]} account for ${a.code} ${a.name}`,
        value: valueOf(a),
        onChange: (v) => setHand({ ...hand, [a.accountId]: v }),
        ...(chart ? { options: options(valueOf(a)) } : {}),
      },
    }),
  });
  const changed = Object.entries(hand).filter(
    ([id, v]) => v.trim() && v !== o.accounts.find((a) => a.accountId === id)?.externalAccount,
  );
  const run = async () => {
    const r = await act.run('exports.runLedgerExport', { target, from: chosen.from, to: chosen.to, mode });
    if (r) setWaiting('export');
  };
  const match = async () => {
    const r = await act.run('exports.matchAccounts', { target });
    if (r) setWaiting('match');
  };
  const saveHand = async () => {
    const mappings = changed.map(([accountId, v]) => ({ accountId, externalAccount: v.trim() }));
    if (!mappings.length) return toast(chart ? 'Choose their account first.' : 'Type their account first.');
    if (await act.run('exports.mapAccounts', { target, mappings }, 'Accounts mapped.')) setHand({});
  };
  const runColumns: DataColumn<Run>[] = [
    {
      id: 'when',
      label: 'When',
      rowHeader: true,
      cell: (r) => ({ text: dayText(dayIn(r.runAt, timeZone)) }),
    },
    { id: 'to', label: 'To', cell: (r) => ({ text: LABEL[r.target] }) },
    {
      id: 'period',
      label: 'Period',
      cell: (r) => ({ text: `${dayText(r.periodStart)} – ${dayText(r.periodEnd)}` }),
    },
    { id: 'detail', label: 'Detail', cell: (r) => ({ text: MODE[r.mode] }) },
    {
      id: 'sent',
      label: 'Sent',
      cell: (r) => ({
        text: String(r.sentCount),
        ...(r.skippedCount ? { lines: [{ text: `${r.skippedCount} already there` }] } : {}),
      }),
    },
    {
      id: 'status',
      label: 'Status',
      cell: (r) => ({
        word: STATUS[r.status],
        ...(r.error ? { lines: [{ text: r.error, tone: 'bad' as const }] } : {}),
      }),
    },
  ];
  return (
    <Section
      title={title}
      state={
        unmatched.length
          ? { mark: 'problem', word: `${accountsWord(unmatched.length)} without a match` }
          : { mark: 'done', word: 'All accounts matched' }
      }
    >
      <div className="bp-acsys">
        <p className="bp-acsys-say" data-muted="">
          Each exported entry keeps its {LABEL[target]} id, so a re-run sends only what is new.
        </p>
        {unmatched.length > 0 && (
          <>
            <Notice
              title={`${accountsWord(unmatched.length)} ${unmatched.length === 1 ? 'has' : 'have'} no ${LABEL[target]} match yet.`}
              {...(canEdit
                ? {
                    actions: (
                      <Link isDisabled={act.busy || waiting === 'match'} onPress={() => void match()}>
                        {waiting === 'match' ? 'Matching…' : 'Match by name'}
                      </Link>
                    ),
                  }
                : {})}
            >
              The export waits until every account with postings has one.
            </Notice>
            {canEdit && (
              <>
                {chart && chart.length > 12 && (
                  <TextField
                    label="Search their chart"
                    type="search"
                    value={search}
                    placeholder="A code or a name"
                    onChange={setSearch}
                  />
                )}
                {!chart && (
                  <p className="bp-acsys-say" data-muted="">
                    Match by name also reads {LABEL[target]}’s chart, so you can pick their account here.
                  </p>
                )}
                <DataTable
                  label="Accounts without a match"
                  columns={[ours, pick(target === 'xero' ? 'Xero account code' : 'QuickBooks account id')]}
                  rows={unmatched.map((a) => ({ ...a, id: a.accountId }))}
                  empty="Every account has a match."
                />
                <div className="bp-acsys-acts">
                  <Link isDisabled={act.busy} onPress={() => void saveHand()}>
                    Map accounts
                  </Link>
                </div>
              </>
            )}
          </>
        )}
        {matched.length > 0 && (
          <Area title="Accounts matched" count={matched.length} level={3}>
            {canEdit && chart ? (
              <>
                <DataTable
                  label="Accounts matched"
                  columns={[ours, pick('Their account')]}
                  rows={matched.map((a) => ({ ...a, id: a.accountId }))}
                  empty="No account has a match yet."
                />
                {unmatched.length === 0 && (
                  <div className="bp-acsys-acts">
                    <Link isDisabled={act.busy || !changed.length} onPress={() => void saveHand()}>
                      Map accounts
                    </Link>
                  </div>
                )}
              </>
            ) : (
              <DataTable
                label="Accounts matched"
                columns={[
                  ours,
                  {
                    id: 'theirs',
                    label: 'Their account',
                    cell: (a) => {
                      const theirs = chart?.find((x) => x.externalId === a.externalAccount);
                      return { text: theirs ? theirLabel(theirs) : (a.externalAccount ?? '') };
                    },
                  },
                ]}
                rows={matched.map((a) => ({ ...a, id: a.accountId }))}
                empty="No account has a match yet."
              />
            )}
          </Area>
        )}
        {canEdit && (
          <div className="bp-acsys-run">
            <div className="bp-acsys-fields">
              <SelectField
                label="Period"
                value={period}
                onChange={setPicked}
                options={choices.map((c) => ({ value: c.value, label: c.label }))}
                {...(picked === null
                  ? {
                      description: o.lastMonth.closed
                        ? `${monthName(o.lastMonth.from)} is closed.`
                        : `${monthName(o.lastMonth.from)} isn’t closed yet, so this month so far.`,
                    }
                  : {})}
              />
              <SelectField
                label="Detail"
                value={mode}
                onChange={(v) => setMode(v as 'summary' | 'detail')}
                options={[
                  { value: 'detail', label: MODE.detail },
                  { value: 'summary', label: MODE.summary },
                ]}
              />
            </div>
            <div className="bp-acsys-acts">
              <Button
                variant="primary"
                isDisabled={act.busy || waiting === 'export' || unmatched.length > 0}
                onPress={() => void run()}
              >
                {waiting === 'export' ? 'Exporting…' : 'Run export'}
              </Button>
            </div>
            {mode === 'summary' && (
              <p className="bp-acsys-say" data-muted="">
                Daily summaries cover closed months only.
              </p>
            )}
          </div>
        )}
        {o.runs.length ? (
          <DataTable
            label="Last exports"
            columns={runColumns}
            rows={o.runs.map((r) => ({ ...r, id: r.exportId }))}
            empty="No exports yet."
          />
        ) : (
          <p className="bp-acsys-say" data-muted="">
            No exports yet.
          </p>
        )}
      </div>
    </Section>
  );
}
